/**
 * Datos de pago de Fina mostrados al cliente cuando debe pagar una cuota.
 * Se usan en `pay-modal` (flujo de pago de cuota) y `credit-blocked-modal`
 * (cuando el crédito está bloqueado) — fuente única para evitar drift entre flujos.
 *
 * Cuando el equipo cambie cuenta o teléfono, basta con tocar este archivo.
 *
 * ## Por qué hay dos juegos de datos (ADM-678, 4ª iteración)
 *
 * Un pago móvil se envía a un **teléfono**; una transferencia, a un **número de
 * cuenta**. Mostrarle la cuenta a alguien que va a pagar por pago móvil es darle
 * un dato que no puede usar. Por eso la fila del medio de la card cambia según
 * el método que el cliente eligió en el paso previo.
 *
 * Banco y RIF son comunes a los dos métodos.
 */
export interface FinaPaymentRow {
    key: string;
    label: string;
    value: string;
    /**
     * Qué se copia al portapapeles cuando es distinto de lo que se muestra. El
     * teléfono se muestra con guion para que sea legible, pero se copia en
     * dígitos corridos: es como lo espera el formulario de cualquier banco.
     */
    copyValue?: string;
}

const BANCO: FinaPaymentRow = { key: 'banco', label: 'BANCO', value: 'Banesco (0134)' };
const RIF: FinaPaymentRow = { key: 'rif', label: 'RIF', value: 'J-504072800' };

/**
 * Teléfono de Fina que recibe los pagos móviles (confirmado por el equipo).
 *
 * No confundir con el teléfono que el formulario le pide al cliente: ese es el
 * **emisor** (desde dónde salió el pago) y es un criterio de búsqueda para
 * Banesco. Este es el **destino**, y solo son instrucciones para el cliente: no
 * participa de la verificación.
 *
 * Se muestra con guion para que sea legible; a los bancos se copia en dígitos
 * corridos (ver `FINA_PAGO_MOVIL_PHONE_DIGITS`).
 */
export const FINA_PAGO_MOVIL_PHONE = '0422-1584450';

/**
 * El mismo teléfono en dígitos corridos, que es lo que se copia al portapapeles:
 * ningún formulario de banco acepta el guion. Se deriva del anterior para que no
 * haya dos números que mantener sincronizados.
 */
export const FINA_PAGO_MOVIL_PHONE_DIGITS = FINA_PAGO_MOVIL_PHONE.replace(/\D/g, '');

/** Card para **pago móvil**: banco, teléfono destino y RIF. */
export const FINA_PAYMENT_DATA_PAGO_MOVIL: ReadonlyArray<FinaPaymentRow> = [
    BANCO,
    {
        key: 'telefono',
        label: 'TELÉFONO',
        value: FINA_PAGO_MOVIL_PHONE,
        copyValue: FINA_PAGO_MOVIL_PHONE_DIGITS,
    },
    RIF,
];

/** Card para **transferencia**: banco, número de cuenta y RIF. */
export const FINA_PAYMENT_DATA_TRANSFERENCIA: ReadonlyArray<FinaPaymentRow> = [BANCO, { key: 'cuenta', label: 'NÚMERO DE CUENTA', value: '01340371603711043284' }, RIF];

/**
 * Datos por defecto. Se mantiene el nombre y la forma anterior —la card de
 * transferencia— para los consumidores que no distinguen método: hoy `payAll`,
 * que no pasa por el paso previo.
 */
export const FINA_PAYMENT_DATA: ReadonlyArray<FinaPaymentRow> = FINA_PAYMENT_DATA_TRANSFERENCIA;
